/**
 * Account shapes, matching `domain-core`'s DTOs.
 *
 * Field names are snake_case because the Java DTOs annotate every accessor with
 * an explicit `@JsonProperty("snake_case")` rather than relying on a naming
 * strategy — the wire format is snake_case even though the Java fields are too.
 */

export type AccountLevel = "ROOT" | "ADMIN" | "USER";
export type AccountStatus = "ACTIVE" | "SUSPENDED" | "DELETED";

export interface AccountCredentialsDTO {
  email: string;
  username: string;
  /** Base64URL-encoded on the wire — see `encodePassword`. */
  password?: string;
}

/**
 * Note the values: the Java enum is `Language { FRENCH, ENGLISH }`, so the wire
 * carries the whole word, not a two-letter code. There is no theme here — the
 * account table has no column for one, and a theme is a property of the screen
 * you are sitting at rather than of you, so it stays a per-device preference.
 */
export type AccountLanguage = "ENGLISH" | "FRENCH";

export interface AccountSettingsDTO {
  language?: AccountLanguage;
}

export interface AccountDataDTO {
  /** Base64 of the raw image bytes — standard alphabet, no `data:` prefix. */
  avatar?: string | null;
}

export interface AccountDTO {
  id: number;
  uuid?: string;
  level: AccountLevel;
  status: AccountStatus;
  credentials: AccountCredentialsDTO;
  settings?: AccountSettingsDTO;
  data?: AccountDataDTO;
  created_at?: string;
  logged_at?: string | null;
  /**
   * Per-page permissions. Deliberately absent from the JWT — the server gates
   * on role and HTTP method, while these drive nav and route guards.
   */
  view_codes: string[];
}

/**
 * One grantable view, as the running app reports it.
 *
 * Fetched rather than kept in a local table: the frontend's hand-maintained
 * copy of nexpim's codes had drifted to 20 of the 32 the server defines, and a
 * picker can only preserve what it can name.
 */
export interface AppViewDescriptor {
  code: string;
  label: string;
  /** SALES, CONFIG, GENERAL, ... derived from the code's first segment. */
  group: string;
}

/** ROOT and ADMIN may mutate; USER is read-only outside `@SafeForUser` endpoints. */
export function canWrite(account: Pick<AccountDTO, "level"> | null): boolean {
  return account?.level === "ROOT" || account?.level === "ADMIN";
}

/** ROOT bypasses view codes entirely, matching `RouteAccessChecker`. */
export function hasView(account: Pick<AccountDTO, "level" | "view_codes"> | null, code: string): boolean {
  if (!account) return false;
  if (account.level === "ROOT") return true;
  return account.view_codes.includes(code);
}

// ── avatars ───────────────────────────────────────────────────────────────

/**
 * A `src` for the account's picture, or null when there is none.
 *
 * The DTO carries bytes, not a MIME type, so the type is read off the image
 * itself: every PNG starts with the same eight-byte signature and every JPEG
 * with `FF D8 FF`, which in Base64 are fixed prefixes. Browsers do sniff past a
 * wrong `data:` type, but only in some contexts, and guessing correctly costs
 * two string comparisons. Existing avatars in the database are a mix of both.
 */
export function avatarSrc(avatar: string | null | undefined): string | null {
  if (!avatar) return null;
  const type = avatar.startsWith("iVBORw0KGgo")
    ? "image/png"
    : avatar.startsWith("/9j/")
      ? "image/jpeg"
      : avatar.startsWith("R0lGOD")
        ? "image/gif"
        : avatar.startsWith("UklGR")
          ? "image/webp"
          : null;
  return type ? `data:${type};base64,${avatar}` : null;
}

/** Two letters for the picture-less case, from the username or the email. */
export function initials(account: Pick<AccountDTO, "credentials"> | null): string {
  const source = account?.credentials.username || account?.credentials.email || "";
  const words = source.split(/[\s._@-]+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

// ── language ──────────────────────────────────────────────────────────────

export function localeOf(language: AccountLanguage | undefined): "en" | "fr" {
  return language === "FRENCH" ? "fr" : "en";
}

export function languageOf(locale: string): AccountLanguage {
  return locale.startsWith("fr") ? "FRENCH" : "ENGLISH";
}
